"use client";

import { useEffect, useRef, useState, type CSSProperties } from "react";

const CHART_IN_VIEW_THRESHOLD = 0.35;

export type AdPerformanceDayCopy = {
  label: string;
  height: number;
};

export type AdPerformanceDemoCopy = {
  chartLabel: string;
  chartAriaLabel: string;
  yAxisLabels: string[];
  days: AdPerformanceDayCopy[];
  highlightDay: string;
};

type CcAdPerformanceDemoProps = {
  copy: AdPerformanceDemoCopy;
  className?: string;
};

export function CcAdPerformanceDemo({ copy, className }: CcAdPerformanceDemoProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const [isChartVisible, setIsChartVisible] = useState(false);
  const [hoveredBar, setHoveredBar] = useState<string | null>(null);
  const [hasChartInteraction, setHasChartInteraction] = useState(false);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsChartVisible(true);
          observer.disconnect();
        }
      },
      { threshold: CHART_IN_VIEW_THRESHOLD },
    );

    observer.observe(chart);

    return () => observer.disconnect();
  }, []);

  const rootClass = ["cc-growth-features__ad-performance", className].filter(Boolean).join(" ");

  return (
    <div className={rootClass}>
      <div className="cc-growth-features__ad-performance-shell">
        <div className="cc-growth-features__ad-performance-header">
          <span className="cc-growth-features__ad-performance-header-label">
            {copy.chartLabel}
          </span>
          <svg
            className="cc-growth-features__ad-performance-header-icon"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M4 19V5M4 19h16M8 17V11M12 17V7M16 17v-4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <div
          ref={chartRef}
          className={[
            "cc-growth-features__ad-performance-chart",
            isChartVisible && "cc-growth-features__ad-performance-chart--visible",
            hasChartInteraction && "cc-growth-features__ad-performance-chart--interacted",
          ]
            .filter(Boolean)
            .join(" ")}
          role="img"
          aria-label={copy.chartAriaLabel}
        >
          <div className="cc-growth-features__ad-performance-y-axis" aria-hidden="true">
            {copy.yAxisLabels.map((label) => (
              <span key={label} className="cc-growth-features__ad-performance-y-label">
                {label}
              </span>
            ))}
          </div>

          <div className="cc-growth-features__ad-performance-plot">
            <div className="cc-growth-features__ad-performance-plot-area">
              <div className="cc-growth-features__ad-performance-gridlines" aria-hidden="true">
                <span />
                <span />
                <span />
                <span />
              </div>

              <div
                className="cc-growth-features__ad-performance-bars"
                aria-hidden="true"
                onMouseLeave={() => setHoveredBar(null)}
              >
                {copy.days.map((bar, index) => {
                  const isDefaultHighlight =
                    bar.label === copy.highlightDay &&
                    (hoveredBar === null || hoveredBar === copy.highlightDay);
                  const isFilled =
                    hoveredBar === null
                      ? bar.label === copy.highlightDay && hasChartInteraction
                      : hoveredBar === bar.label;

                  return (
                    <div
                      key={bar.label}
                      className="cc-growth-features__ad-performance-bar-cell"
                      onMouseEnter={() => {
                        setHasChartInteraction(true);
                        setHoveredBar(bar.label);
                      }}
                    >
                      <div
                        className="cc-growth-features__ad-performance-bar"
                        style={
                          {
                            "--bar-height": `${bar.height}%`,
                            "--bar-index": index,
                          } as CSSProperties
                        }
                      >
                        <div
                          className={[
                            "cc-growth-features__ad-performance-bar-track",
                            isDefaultHighlight &&
                              "cc-growth-features__ad-performance-bar-track--highlight",
                            isFilled && "cc-growth-features__ad-performance-bar-track--filled",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        >
                          <div className="cc-growth-features__ad-performance-bar-fill" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="cc-growth-features__ad-performance-x-axis" aria-hidden="true">
              {copy.days.map((bar) => (
                <span key={bar.label} className="cc-growth-features__ad-performance-x-label">
                  {bar.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
